import { Keyboard } from '@capacitor/keyboard';
import { isAndroid } from './platform';

/**
 * The on-screen keyboard's height on Android, published on the document root: the keyboard's
 * height while it is up, 0px while it is down. Unset off Android.
 */
const HEIGHT_VAR = '--keyboard-height';
export const KEYBOARD_HEIGHT = `var(${HEIGHT_VAR}, 0px)`;

/** The Chromium VirtualKeyboard API, not in TypeScript's DOM types yet. */
type VirtualKeyboardNavigator = Navigator & { virtualKeyboard?: { overlaysContent: boolean } };

/** Fields a tap raises the on-screen keyboard for. A select opens a picker instead. */
const TYPED =
    'textarea, input:not([type="checkbox"]):not([type="radio"]):not([type="button"]):not([type="submit"]):not([type="reset"]):not([type="range"]):not([type="file"]):not([type="color"])';

export const raisesKeyboard = (element: Element | null): element is HTMLElement =>
    element instanceof HTMLElement && (element.isContentEditable || element.matches(TYPED));

let open = false;
let height = 0;
const subscribers = new Set<() => void>();

/**
 * Scrolls the focused field into view when the keyboard covers it. The window no longer shrinks
 * for the keyboard, so the WebView takes the field for visible and does not scroll to it itself.
 */
const revealFocusedField = () => {
    const field = document.activeElement;
    if (!raisesKeyboard(field)) return;
    if (field.getBoundingClientRect().bottom > globalThis.window.innerHeight - height) {
        field.scrollIntoView({ block: 'nearest' });
    }
};

/**
 * Follows the on-screen keyboard on Android, where it overlays the page: the window keeps its
 * size (MainActivity), so the page reads the keyboard's height from the Keyboard plugin and keeps
 * its foot clear of it through `--keyboard-height`. The plugin's events come as the keyboard
 * starts to move, and the variable and the open state change together in the same task.
 *
 * The WebView's own `env(keyboard-inset-height)` stays at 0 here, so the plugin is the source.
 * `overlaysContent` tells a WebView that has the VirtualKeyboard API not to resize the visual
 * viewport for the keyboard either.
 *
 * A focused field the keyboard covers is scrolled into view: once the keyboard has risen and the
 * page has laid itself out for it (a frame later), and on each focus change while it is up.
 *
 * Call once at startup. It does nothing off Android.
 */
export const startKeyboard = (): void => {
    if (!isAndroid()) return;
    const { virtualKeyboard } = navigator as VirtualKeyboardNavigator;
    if (virtualKeyboard) virtualKeyboard.overlaysContent = true;

    const root = document.documentElement.style;
    const announce = (next: boolean) => {
        open = next;
        subscribers.forEach((subscriber) => subscriber());
    };
    Keyboard.addListener('keyboardWillShow', ({ keyboardHeight }) => {
        height = keyboardHeight;
        root.setProperty(HEIGHT_VAR, `${keyboardHeight}px`);
        announce(true);
        requestAnimationFrame(revealFocusedField);
    });
    Keyboard.addListener('keyboardWillHide', () => {
        height = 0;
        root.setProperty(HEIGHT_VAR, '0px');
        announce(false);
    });
    document.addEventListener('focusin', () => {
        if (open) revealFocusedField();
    });
};

/** Calls `onChange` each time the keyboard rises or goes down. Returns the unsubscribe. */
export const subscribeKeyboard = (onChange: () => void): (() => void) => {
    subscribers.add(onChange);
    return () => {
        subscribers.delete(onChange);
    };
};

/** Whether the keyboard is up. Always false off Android. */
export const isKeyboardOpen = (): boolean => open;
